import { useEffect, useMemo, useState } from 'react'
import { CalendarDays, Download, Loader2, RefreshCw } from 'lucide-react'
import api from '../../utils/api'
import { exportRowsToExcel } from '../../utils/exportExcel'

const DEFAULT_START_DATE = '2026-04-01'

function formatAmount(value) {
  const amount = Number(value || 0)
  return Number.isFinite(amount) ? amount.toFixed(2) : '0.00'
}

function formatPercent(value) {
  const percent = Number(value || 0)
  return `${Number.isFinite(percent) ? percent.toFixed(2) : '0.00'}%`
}

export default function DailyReportsTab() {
  const [loading, setLoading] = useState(true)
  const [startDate, setStartDate] = useState(DEFAULT_START_DATE)
  const [endDate, setEndDate] = useState('')
  const [filters, setFilters] = useState({ startDate: DEFAULT_START_DATE, endDate: '' })
  const [latestDate, setLatestDate] = useState('')
  const [list, setList] = useState([])

  const fetchData = async (nextFilters = filters) => {
    setLoading(true)
    try {
      const response = await api.get('/admin/reporting-reports/daily', {
        params: {
          start_date: nextFilters.startDate || undefined,
          end_date: nextFilters.endDate || undefined
        }
      })
      if (response.data.success) {
        const data = response.data.data || response.data || {}
        setLatestDate(data.latest_date || '')
        setList(Array.isArray(data.list) ? data.list : [])
      }
    } catch (error) {
      console.error('获取每日报表失败:', error)
      setList([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(filters)
  }, [])

  const handleSubmit = (event) => {
    event.preventDefault()
    const nextFilters = {
      startDate: startDate || DEFAULT_START_DATE,
      endDate: endDate || ''
    }
    setFilters(nextFilters)
    fetchData(nextFilters)
  }

  const tableRows = useMemo(() => {
    return [...list].sort((a, b) => String(b.stat_date || '').localeCompare(String(a.stat_date || '')))
  }, [list])

  const handleExport = () => {
    if (tableRows.length === 0) {
      return
    }

    exportRowsToExcel({
      fileName: `每日报表_${filters.startDate || DEFAULT_START_DATE}_${filters.endDate || latestDate || 'latest'}`,
      sheetName: '每日报表',
      columns: [
        { header: '日期', key: 'stat_date', width: 14 },
        { header: '日活', key: 'dau', width: 10 },
        { header: '周活', key: 'wau', width: 10 },
        { header: '月活', key: 'mau', width: 10 },
        { header: '注册', key: 'registered_count', width: 10 },
        { header: '首充', key: 'first_recharge_user_count', width: 10 },
        { header: '二充', key: 'repeat_recharge_user_count', width: 10 },
        { header: '首充率%', value: (row) => formatPercent(row.first_recharge_rate), width: 12 },
        { header: '二充率%', value: (row) => formatPercent(row.repeat_recharge_rate), width: 12 },
        { header: '30日留存', value: (row) => `${row.active_user_count_after_30d_registration || 0}/${row.registered_total_before_30d || 0}`, width: 14 },
        { header: '留存率', value: (row) => formatPercent(row.retention_rate), width: 12 },
        { header: '充值金额', value: (row) => formatAmount(row.daily_recharge_amount), width: 14 },
        { header: '累计充值金额', value: (row) => formatAmount(row.cumulative_recharge_amount_since_start), width: 16 },
        { header: '月累计充值金额', value: (row) => formatAmount(row.monthly_cumulative_recharge_amount), width: 16 }
      ],
      rows: tableRows
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">每日报表</h2>
          <p className="mt-1 text-sm text-gray-500">展示日报核心指标、转化率与累计充值金额</p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <CalendarDays className="h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              className="border-0 bg-transparent text-sm text-gray-700 outline-none"
            />
          </div>

          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <CalendarDays className="h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              className="border-0 bg-transparent text-sm text-gray-700 outline-none"
            />
          </div>

          <button type="submit" className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">
            查询
          </button>
          <button
            type="button"
            onClick={() => fetchData(filters)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            刷新
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={loading || tableRows.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
          >
            <Download className="h-4 w-4" />
            导出 Excel
          </button>
        </form>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 text-sm text-gray-600 shadow-sm">
        最新统计日期：{latestDate || '--'}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-[1680px] divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">日期</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">日活</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">周活</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">月活</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">注册</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">首充</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">二充</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">首充率%</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">二充率%</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">30日留存</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">留存率</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">充值金额</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">累计充值金额</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">月累计充值金额</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={14} className="px-6 py-10 text-center text-sm text-gray-500">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-blue-600" />
                  </td>
                </tr>
              ) : tableRows.length > 0 ? (
                tableRows.map((item) => (
                  <tr key={item.stat_date} className="hover:bg-gray-50">
                    <td className="px-4 py-4 text-sm text-gray-900">{item.stat_date}</td>
                    <td className="px-4 py-4 text-right text-sm text-gray-900">{item.dau || 0}</td>
                    <td className="px-4 py-4 text-right text-sm text-gray-900">{item.wau || 0}</td>
                    <td className="px-4 py-4 text-right text-sm text-gray-900">{item.mau || 0}</td>
                    <td className="px-4 py-4 text-right text-sm font-medium text-blue-600">{item.registered_count || 0}</td>
                    <td className="px-4 py-4 text-right text-sm font-medium text-emerald-600">{item.first_recharge_user_count || 0}</td>
                    <td className="px-4 py-4 text-right text-sm font-medium text-violet-600">{item.repeat_recharge_user_count || 0}</td>
                    <td className="px-4 py-4 text-right text-sm text-gray-900">{formatPercent(item.first_recharge_rate)}</td>
                    <td className="px-4 py-4 text-right text-sm text-gray-900">{formatPercent(item.repeat_recharge_rate)}</td>
                    <td className="px-4 py-4 text-right text-sm font-medium text-amber-600">{(item.active_user_count_after_30d_registration || 0)}/{(item.registered_total_before_30d || 0)}</td>
                    <td className="px-4 py-4 text-right text-sm text-gray-900">{formatPercent(item.retention_rate)}</td>
                    <td className="px-4 py-4 text-right text-sm font-medium text-gray-900">{formatAmount(item.daily_recharge_amount)}</td>
                    <td className="px-4 py-4 text-right text-sm text-gray-900">{formatAmount(item.cumulative_recharge_amount_since_start)}</td>
                    <td className="px-4 py-4 text-right text-sm text-gray-900">{formatAmount(item.monthly_cumulative_recharge_amount)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={14} className="px-6 py-10 text-center text-sm text-gray-500">
                    当前区间暂无数据
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
